// dependencies
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
	GetCompanyReviewFormResponseJson,
	GetCompanyReviewFormsResponse,
	GetReviewFormsResponseJson,
	PatchCompanyReviewFormRequestJson,
	PostCompanyReviewFormRequest,
} from "@performyard/service-contracts";

import { ReviewQuestion } from "../../../pages/Administration/Reviews/EditForm/utils";

import {
	ReviewFormsQueryParams,
	allReviewFormsQuery,
	reviewFormsQuery,
	useCompanyFormQuery,
	useCompanyFormsQuery,
} from "../../../api/queries";

//api
import { IReviewFormData, getFormPreview, getFormPreviewPDF } from "../../services";
import { deleteCompanyForm, patchCompanyForm, postCompanyForm } from "../../services/defs/internal-api";
//utils
import { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

export const useReviewForms = (params: ReviewFormsQueryParams, options?: PYUseQueryOptions<IReviewFormData>) => {
	return useQuery(reviewFormsQuery(params, options));
};

export const useAllReviewForms = (options?: PYUseQueryOptions<GetReviewFormsResponseJson>) => {
	return useQuery(allReviewFormsQuery(options));
};

export const useCompanyForms = (
	searchOptions: { search?: string; sort?: string; direction?: string; page?: number; limit?: number },
	options?: PYUseQueryOptions<GetCompanyReviewFormsResponse>
) => {
	return useQuery(useCompanyFormsQuery(searchOptions, options));
};

export const useCompanyForm = (baseId: string, options?: PYUseQueryOptions<GetCompanyReviewFormResponseJson>) => {
	return useQuery(useCompanyFormQuery(baseId, options));
};

export const useCreateCompanyForm = (options?: PYUseMutationOptions) => {
	const queryCache = useQueryClient();
	return useMutation({
		mutationKey: ["add company forms"],
		mutationFn: async (props: PostCompanyReviewFormRequest) => postCompanyForm(props),
		...options,
		onSuccess: (response, variables, context) => {
			queryCache.invalidateQueries({ queryKey: ["company-forms"] });
			options?.onSuccess && options.onSuccess(response, variables, context);
		},
	});
};

export const useMutateCompanyForm = (options?: PYUseMutationOptions) => {
	const queryCache = useQueryClient();
	return useMutation({
		mutationKey: ["add company forms"],
		mutationFn: async ({ baseId, updates }: { baseId: string; updates: PatchCompanyReviewFormRequestJson }) =>
			patchCompanyForm(baseId, updates),
		...options,
		onSuccess: (response, variables, context) => {
			queryCache.invalidateQueries({ queryKey: ["company-forms"] });
			queryCache.invalidateQueries({ queryKey: ["company-form", variables.baseId] });
			options?.onSuccess && options.onSuccess(response, variables, context);
		},
	});
};

export const useDeleteCompanyForm = (options?: PYUseMutationOptions) => {
	const queryCache = useQueryClient();
	return useMutation({
		mutationKey: ["delete company forms"],
		mutationFn: async (id: string) => deleteCompanyForm(id),
		...options,
		onSuccess: (response, variables, context) => {
			queryCache.invalidateQueries({ queryKey: ["company-forms"] });
			options?.onSuccess && options.onSuccess(response, variables, context);
		},
	});
};

export const useGetPreviewForm = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationKey: ["get preview form"],
		mutationFn: async ({ questions, name }: { questions: ReviewQuestion[]; name: string }) =>
			getFormPreview(questions, name),
		...options,
	});
};

export const useGetPreviewFormPDF = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationKey: ["get preview form"],
		mutationFn: async ({ questions, name }: { questions: ReviewQuestion[]; name: string }) =>
			getFormPreviewPDF(questions, name),
		...options,
	});
};
